import { Fragment, h } from '@stencil/core';
import { handleFilledIcon, isShopAvailable } from '@phn/utility/helper/shop-helper';
import { navContentStore } from '@phn/state/nav-content-store';
import { shopStateStore } from '@phn/state/shop-state-store';
import { pureButtonSkeleton, pureButtonSkeletonDark } from '../../utility/skeleton';
import { NavigationMode, NavigationTheme } from '@phn/utility/constants';
import { openContextualDrawer } from '@phn/services/navigation-drawer-service';
import { sendDataLayerEventShoppingCart } from '@phn/services/analytics/send-data-layer-events-service';
import { ContextualIconProps, ContextualIconResponse } from '@phn/utility/constants/app';
import { getRightTheme } from '@phn/utility/helper';
import featureToggles from '@phn/services/feature-toggles';
import animationManager from '@phn/services/animation-manager';
import { BadgeCount } from './badge-count';

export default function shopIcons({
  locale,
  app,
  mode,
  theme,
  isNavigationLoaded,
  isDesktop
}: ContextualIconProps): ContextualIconResponse {
  const { shopInformation } = shopStateStore.state;
  const { shop } = navContentStore.state;
  const shopCartLink = shop?.cart || '';
  const shopWishlistLink = shop?.wishlist?.link || '';

  function getAriaLabelForShoppingBag(): string {
    return `My shopping bag, ${shopInformation.numberOfShoppingItems} items, estimated subtotal ${shopInformation.subTotal}`;
  }
  function getAriaLabelForWishlist(): string {
    return `My wishlist, ${shopInformation.wishlistItemsNumber} items`;
  }

  return {
    leftIcons: isShopAvailable(shop, locale, app) ? (
      <div class="icon">
        <phn-p-button-pure
          icon="search"
          hide-label="true"
          stretch="true"
          aria="{ 'aria-haspopup': 'dialog', 'aria-label' : 'open the shop search field' }"
          data-test-id="search-button"
          onClick={async () => {
            if (featureToggles.isNaviDrawerFromCDNEnabled()) {
              await openContextualDrawer();
            } else {
              await animationManager.play('openContextualDrawer');
              sendDataLayerEventShoppingCart('mainmenu.shop.search', 'Shop search');
            }
          }}
          style={isNavigationLoaded ? {} : { display: 'none' }}
          theme={getRightTheme(theme, mode)}
        />
        {!isNavigationLoaded
          ? mode === NavigationMode.hero && theme === NavigationTheme.dark
            ? pureButtonSkeletonDark()
            : pureButtonSkeleton()
          : null}
      </div>
    ) : null,
    rightIcons: isShopAvailable(shop, locale, app) ? (
      <Fragment>
        {isDesktop ? (
          <div class="icon">
            <phn-p-link-pure
              target={'_self'}
              hide-label="true"
              icon="none"
              theme={getRightTheme(theme, mode)}
              style={isNavigationLoaded ? {} : { display: 'none' }}
              className="wishlist-link"
              data-test-id="wishlist-link"
              onClick={() => {
                sendDataLayerEventShoppingCart('mainmenu.shop.wishlist', 'Wishlist', shopWishlistLink);
              }}
            >
              <a href={shopWishlistLink} aria-label={getAriaLabelForWishlist()}>
                <div class="icon-flex">
                  <BadgeCount numberOfItems={shopInformation.wishlistItemsNumber} theme={theme} mode={mode} />
                  <phn-p-icon
                    theme={getRightTheme(theme, mode)}
                    name={handleFilledIcon('heart', shopInformation.wishlistItemsNumber)}
                    data-test-id="wishlist-link-icon"
                  />
                </div>
              </a>
            </phn-p-link-pure>
          </div>
        ) : null}
        {!isNavigationLoaded && isDesktop
          ? mode === NavigationMode.hero && theme === NavigationTheme.dark
            ? pureButtonSkeletonDark()
            : pureButtonSkeleton()
          : null}

        <div class="icon">
          <phn-p-link-pure
            target={'_self'}
            hide-label="true"
            icon="none"
            theme={getRightTheme(theme, mode)}
            style={isNavigationLoaded ? {} : { display: 'none' }}
            class="shopping-cart-link"
            data-test-id="shopping-cart-link"
            onClick={() => {
              sendDataLayerEventShoppingCart('mainmenu.shop.shoppingbag', 'Shopping cart', shopCartLink);
            }}
          >
            <a href={shopCartLink} aria-label={getAriaLabelForShoppingBag()}>
              <div class="icon-flex">
                <BadgeCount numberOfItems={shopInformation.numberOfShoppingItems} theme={theme} mode={mode} />
                <phn-p-icon
                  theme={getRightTheme(theme, mode)}
                  name={handleFilledIcon('shopping-bag', shopInformation.numberOfShoppingItems)}
                  data-test-id="shopping-cart-link-icon"
                />
              </div>
            </a>
          </phn-p-link-pure>
          {!isNavigationLoaded
            ? mode === NavigationMode.hero && theme === NavigationTheme.dark
              ? pureButtonSkeletonDark()
              : pureButtonSkeleton()
            : null}
        </div>
      </Fragment>
    ) : null
  };
}
